import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ThemeProvider, createTheme, CssBaseline, Box, Typography, Collapse,
  Tabs, Tab,
} from "@mui/material";
import api from "../api/api";

// ─── MUI Theme ────────────────────────────────────────────────────────────────
const muiTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#6c7ef8" },
    background: { default: "#0d0e14", paper: "#13151e" },
    text: { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});

// ─── Design Tokens ────────────────────────────────────────────────────────────
const D = {
  bg: "#0d0e14", surface: "#13151e", surfaceUp: "#191c28",
  border: "rgba(255,255,255,0.07)", borderMed: "rgba(255,255,255,0.11)",
  accent: "#6c7ef8", accentSoft: "rgba(108,126,248,0.1)", accentBorder: "rgba(108,126,248,0.2)",
  success: "#34d399", successSoft: "rgba(52,211,153,0.1)",
  danger: "#f87171", dangerSoft: "rgba(248,113,113,0.1)",
  warn: "#f59e0b", warnSoft: "rgba(245,158,11,0.1)",
  purple: "#a37bf8", textPrimary: "#f0f2f7", textSec: "#b0b7ce", textMuted: "#6b7390",
  fontDisplay: "'Plus Jakarta Sans','DM Sans',sans-serif",
  fontBody: "'DM Sans',sans-serif", fontMono: "'JetBrains Mono',monospace",
};

const PLATFORM_META = {
  linkedin:  { label: "LinkedIn",  icon: "💼", color: "#0A66C2", soft: "rgba(10,102,194,0.12)",  border: "rgba(10,102,194,0.3)",  note: "Personal profile & Company pages" },
  instagram: { label: "Instagram", icon: "📸", color: "#E1306C", soft: "rgba(225,48,108,0.12)",  border: "rgba(225,48,108,0.3)",  note: "Business or Creator accounts only" },
  facebook:  { label: "Facebook",  icon: "📘", color: "#1877F2", soft: "rgba(24,119,242,0.12)",  border: "rgba(24,119,242,0.3)",  note: "Facebook Pages only" },
};

const ALL_PLATFORMS = ["linkedin", "instagram", "facebook"];

const TIMEZONES = [
  "UTC","America/New_York","America/Chicago","America/Denver","America/Los_Angeles",
  "America/Anchorage","America/Honolulu","America/Sao_Paulo","America/Toronto",
  "Europe/London","Europe/Paris","Europe/Berlin","Europe/Madrid","Europe/Rome",
  "Europe/Amsterdam","Europe/Stockholm","Europe/Warsaw","Europe/Istanbul",
  "Asia/Dubai","Asia/Kolkata","Asia/Dhaka","Asia/Bangkok","Asia/Singapore",
  "Asia/Shanghai","Asia/Tokyo","Asia/Seoul","Australia/Sydney","Australia/Melbourne",
  "Pacific/Auckland",
];

const CONTENT_PLATFORM_MAP = { linkedin: "linkedin", instagram: "instagram", facebook: "facebook" };

// ─── Global Styles ─────────────────────────────────────────────────────────────
const G = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { background: #0d0e14; color: #f0f2f7; font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
  ::selection { background: rgba(108,126,248,0.25); }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 99px; }

  @keyframes fadeUp    { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes scaleIn   { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
  @keyframes modalIn   { from { opacity:0; transform:scale(0.96) translateY(20px); } to { opacity:1; transform:scale(1) translateY(0); } }
  @keyframes spin      { to { transform: rotate(360deg); } }
  @keyframes logoGlow  { 0%,100%{box-shadow:0 0 16px rgba(108,126,248,0.3)} 50%{box-shadow:0 0 28px rgba(163,123,248,0.45)} }
  @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes shimmer   { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  @keyframes tagIn     { from { opacity:0; transform:scale(0.85); } to { opacity:1; transform:scale(1); } }
  @keyframes progressFill { from { width:0; } to { width:var(--target-w); } }

  .fade-up { animation: fadeUp 0.4s cubic-bezier(0.22,1,0.36,1) both; }
  .fade-up-1 { animation-delay: 0.06s; }
  .fade-up-2 { animation-delay: 0.12s; }
  .fade-up-3 { animation-delay: 0.18s; }

  .ap-card { background: #13151e; border: 1px solid rgba(255,255,255,0.07); border-radius: 14px; transition: border-color 0.2s; }
  .ap-card:hover { border-color: rgba(255,255,255,0.12); }

  .platform-tile {
    background: #13151e; border: 1px solid rgba(255,255,255,0.07); border-radius: 14px;
    padding: 22px; transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
    cursor: default; position: relative; overflow: hidden;
  }
  .platform-tile::before {
    content: ''; position: absolute; inset: 0; border-radius: 14px;
    background: linear-gradient(135deg, rgba(108,126,248,0.04) 0%, transparent 60%);
    opacity: 0; transition: opacity 0.2s; pointer-events: none;
  }
  .platform-tile.connected { border-color: rgba(52,211,153,0.3); }
  .platform-tile.connected::before { background: linear-gradient(135deg, rgba(52,211,153,0.06) 0%, transparent 60%); opacity: 1; }

  .content-gen-card { background: #13151e; border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; margin-bottom: 10px; overflow: hidden; transition: border-color 0.2s; }
  .content-gen-card:hover { border-color: rgba(255,255,255,0.13); }
  .content-gen-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; cursor: pointer; transition: background 0.15s; }
  .content-gen-head:hover { background: rgba(255,255,255,0.02); }

  .post-preview-card { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07); border-radius: 10px; padding: 14px 16px; margin-bottom: 8px; position: relative; transition: border-color 0.2s; }
  .post-preview-card:hover { border-color: rgba(255,255,255,0.12); }
  .post-preview-card.selected { border-color: rgba(108,126,248,0.4); background: rgba(108,126,248,0.06); }

  .ap-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; padding: 10px 20px; border-radius: 9px; font-family: 'DM Sans', sans-serif; font-size: 13.5px; font-weight: 700; cursor: pointer; border: none; transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1); }
  .ap-btn:active { transform: scale(0.96); }
  .ap-btn-primary { background: linear-gradient(135deg, #6c7ef8, #a37bf8); color: white; }
  .ap-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(108,126,248,0.35); }
  .ap-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
  .ap-btn-ghost { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); color: #b0b7ce; }
  .ap-btn-ghost:hover { background: rgba(255,255,255,0.07); border-color: rgba(255,255,255,0.16); color: #f0f2f7; }
  .ap-btn-danger { background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25); color: #f87171; }
  .ap-btn-danger:hover { background: rgba(248,113,113,0.18); border-color: rgba(248,113,113,0.4); transform: translateY(-1px); }
  .ap-btn-sm { padding: 7px 14px; font-size: 12.5px; border-radius: 8px; }
  .ap-btn-green { background: rgba(52,211,153,0.12); border: 1px solid rgba(52,211,153,0.3); color: #34d399; }
  .ap-btn-green:hover { background: rgba(52,211,153,0.2); border-color: rgba(52,211,153,0.45); transform: translateY(-1px); }

  .platform-chip { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; cursor: pointer; border: 1.5px solid transparent; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1); user-select: none; background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.09); color: #8b91a8; }
  .platform-chip:hover { transform: translateY(-1px); }
  .platform-chip.active { color: white; }

  .schedule-toggle { display: flex; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 4px; gap: 4px; }
  .schedule-toggle-btn { flex: 1; padding: 9px 16px; border-radius: 8px; border: none; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; color: #8b91a8; background: transparent; }
  .schedule-toggle-btn.active { background: rgba(108,126,248,0.18); color: #f0f2f7; border: 1px solid rgba(108,126,248,0.35); }
  .schedule-toggle-btn:hover:not(.active) { background: rgba(255,255,255,0.04); color: #c0c7de; }

  .ap-select { width: 100%; padding: 11px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; color: #f0f2f7; font-family: 'DM Sans', sans-serif; font-size: 13.5px; outline: none; cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238b91a8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 38px; transition: border-color 0.2s, box-shadow 0.2s; }
  .ap-select:focus { border-color: rgba(108,126,248,0.5); box-shadow: 0 0 0 3px rgba(108,126,248,0.12); }
  .ap-select option { background: #191c28; }

  .ap-datetime { width: 100%; padding: 11px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; color: #f0f2f7; font-family: 'DM Sans', sans-serif; font-size: 13.5px; outline: none; transition: border-color 0.2s, box-shadow 0.2s; color-scheme: dark; }
  .ap-datetime:focus { border-color: rgba(108,126,248,0.5); box-shadow: 0 0 0 3px rgba(108,126,248,0.12); }

  .scheduled-row { background: #13151e; border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; padding: 18px 20px; margin-bottom: 10px; animation: fadeUp 0.3s both; transition: border-color 0.2s; }
  .scheduled-row:hover { border-color: rgba(255,255,255,0.12); }
  .history-row { background: #13151e; border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; padding: 18px 20px; margin-bottom: 10px; animation: fadeUp 0.3s both; transition: border-color 0.2s; }
  .history-row:hover { border-color: rgba(255,255,255,0.12); }

  .sk { background: rgba(255,255,255,0.05); background-image: linear-gradient(90deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.08) 300px, rgba(255,255,255,0.04) 600px); background-size: 600px; animation: shimmer 1.6s ease infinite; border-radius: 10px; }
  .chevron { transition: transform 0.25s; display: inline-block; }
  .chevron.open { transform: rotate(180deg); }
  .status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
  .status-dot.connected { background: #34d399; box-shadow: 0 0 6px rgba(52,211,153,0.5); animation: pulse 2s infinite; }
  .status-dot.disconnected { background: rgba(255,255,255,0.2); }
  .alert-box { border-radius: 10px; padding: 14px 18px; display: flex; gap: 12px; align-items: flex-start; }
  .alert-box.success { background: rgba(52,211,153,0.08); border: 1px solid rgba(52,211,153,0.25); }
  .alert-box.error   { background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25); }
  .alert-box.info    { background: rgba(108,126,248,0.08); border: 1px solid rgba(108,126,248,0.25); }
  .alert-box.warn    { background: rgba(245,158,11,0.08);  border: 1px solid rgba(245,158,11,0.25); }

  /* ── Hashtag chips ── */
  .ht-chip-existing {
    display:inline-flex; align-items:center; padding:4px 11px; border-radius:6px;
    font-family:'JetBrains Mono',monospace; font-size:12px; font-weight:500;
    background:rgba(108,126,248,0.1); border:1px solid rgba(108,126,248,0.22); color:#818cf8;
    user-select:none; animation: tagIn 0.25s both;
  }
  .ht-chip-suggestion {
    display:inline-flex; align-items:center; gap:5px; padding:5px 12px; border-radius:6px;
    font-family:'JetBrains Mono',monospace; font-size:12px; font-weight:600;
    cursor:pointer; transition:all 0.18s; animation: tagIn 0.25s both;
    background:rgba(52,211,153,0.07); border:1px solid rgba(52,211,153,0.18); color:#6ee7b7;
  }
  .ht-chip-suggestion:hover { background:rgba(52,211,153,0.14); border-color:rgba(52,211,153,0.35); transform:translateY(-1px); }
  .ht-chip-suggestion.selected { background:rgba(52,211,153,0.18); border-color:rgba(52,211,153,0.45); color:#34d399; }
  .ht-chip-sk { display:inline-block; width:70px; height:26px; border-radius:6px; }

  /* ── Calendar modal ── */
  .cal-modal-overlay {
    position:fixed; inset:0; z-index:1000;
    background:rgba(0,0,0,0.72); backdrop-filter:blur(8px);
    display:flex; align-items:center; justify-content:center; padding:20px;
    animation: fadeUp 0.2s both;
  }
  .cal-modal {
    background:#13151e; border:1px solid rgba(255,255,255,0.1);
    border-radius:20px; width:100%; max-width:920px; max-height:88vh;
    display:flex; flex-direction:column; overflow:hidden;
    animation: modalIn 0.3s cubic-bezier(0.22,1,0.36,1) both;
    box-shadow: 0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06);
  }
  .cal-modal-header {
    padding:24px 28px 20px; border-bottom:1px solid rgba(255,255,255,0.07);
    flex-shrink:0;
  }
  .cal-modal-body { flex:1; overflow-y:auto; padding:20px 28px; }
  .cal-modal-footer {
    padding:18px 28px; border-top:1px solid rgba(255,255,255,0.07);
    flex-shrink:0; background:#13151e;
  }
  .cal-slot-row {
    display:flex; gap:14px; align-items:flex-start; padding:14px 0;
    border-bottom:1px solid rgba(255,255,255,0.05);
  }
  .cal-slot-row:last-child { border-bottom:none; }
  .cal-slot-toggle {
    width:20px; height:20px; border-radius:5px; border:2px solid rgba(255,255,255,0.2);
    background:transparent; cursor:pointer; flex-shrink:0; margin-top:2px;
    transition:all 0.18s; display:flex; align-items:center; justify-content:center;
  }
  .cal-slot-toggle.on { background:#6c7ef8; border-color:#6c7ef8; }
  .progress-bar-wrap { height:4px; background:rgba(255,255,255,0.06); border-radius:99px; overflow:hidden; }
  .progress-bar-fill { height:100%; background:linear-gradient(90deg,#6c7ef8,#a37bf8); border-radius:99px; transition:width 0.4s ease; }

  .MuiTab-root { text-transform: none !important; font-family: 'DM Sans', sans-serif !important; font-weight: 500 !important; font-size: 13.5px !important; min-height: 44px !important; color: #b0b7ce !important; }
  .MuiTab-root.Mui-selected { color: #f0f2f7 !important; font-weight: 700 !important; }
  .MuiTabs-indicator { background: linear-gradient(90deg, #6c7ef8, #a37bf8) !important; height: 2px !important; border-radius: 2px !important; }

  /* ── Image picker (Create Post) ── */
  @keyframes imgIn { from { opacity:0; transform:scale(0.88); } to { opacity:1; transform:scale(1); } }
  @keyframes checkPop { 0%{transform:scale(0)} 70%{transform:scale(1.25)} 100%{transform:scale(1)} }

  .img-picker-wrap {
    background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.07);
    border-radius: 14px; padding: 16px; animation: scaleIn 0.25s cubic-bezier(0.22,1,0.36,1) both;
  }
  .img-picker-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
  .img-picker-label { display:flex; align-items:center; gap:8px; }
  .img-picker-label-icon {
    width:28px; height:28px; border-radius:7px; background:rgba(108,126,248,0.12);
    border:1px solid rgba(108,126,248,0.25); display:flex; align-items:center; justify-content:center; font-size:13px;
  }
  .img-picker-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px;
  }
  .img-picker-thumb {
    aspect-ratio: 1; border-radius: 10px; overflow: hidden; cursor: pointer;
    border: 2px solid rgba(255,255,255,0.06); transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
    position: relative; background: rgba(255,255,255,0.03);
    animation: imgIn 0.3s cubic-bezier(0.22,1,0.36,1) both;
  }
  .img-picker-thumb:nth-child(1){animation-delay:0.04s}
  .img-picker-thumb:nth-child(2){animation-delay:0.08s}
  .img-picker-thumb:nth-child(3){animation-delay:0.12s}
  .img-picker-thumb:nth-child(4){animation-delay:0.16s}
  .img-picker-thumb:nth-child(5){animation-delay:0.20s}
  .img-picker-thumb:hover { border-color: rgba(108,126,248,0.55); transform: scale(1.05); box-shadow: 0 8px 20px rgba(0,0,0,0.35); }
  .img-picker-thumb.selected {
    border-color: #6c7ef8; box-shadow: 0 0 0 3px rgba(108,126,248,0.22), 0 8px 20px rgba(0,0,0,0.4);
    transform: scale(1.03);
  }
  .img-picker-thumb img { width: 100%; height: 100%; object-fit: contain; display: block; transition: filter 0.2s; }
  .img-picker-thumb.selected img { filter: brightness(0.82); }
  .img-picker-thumb .img-overlay {
    position:absolute; inset:0; background:linear-gradient(135deg,rgba(108,126,248,0.18),rgba(163,123,248,0.1));
    opacity:0; transition:opacity 0.2s; pointer-events:none;
  }
  .img-picker-thumb:hover .img-overlay { opacity:1; }
  .img-picker-thumb .img-check {
    position:absolute; top:6px; right:6px; width:22px; height:22px; border-radius:50%;
    background:linear-gradient(135deg,#6c7ef8,#a37bf8);
    display:flex; align-items:center; justify-content:center;
    font-size:11px; color:white; font-weight:800;
    box-shadow:0 2px 8px rgba(108,126,248,0.5);
    animation: checkPop 0.3s cubic-bezier(0.34,1.56,0.64,1) both;
  }
  .img-picker-thumb .img-num {
    position:absolute; bottom:5px; left:6px; font-size:9px; font-weight:700;
    color:rgba(255,255,255,0.7); font-family:'JetBrains Mono',monospace;
    background:rgba(0,0,0,0.5); border-radius:3px; padding:1px 5px; line-height:1.6;
  }
  .img-selected-preview {
    display:flex; align-items:flex-start; flex-direction:column; gap:12px; padding:14px;
    background:rgba(108,126,248,0.07); border:1px solid rgba(108,126,248,0.2);
    border-radius:10px; margin-top:12px; animation: scaleIn 0.2s both;
  }
  .img-selected-preview img { width:100%; height:480px; border-radius:12px; object-fit:contain; box-shadow:0 8px 32px rgba(0,0,0,0.55); }

  /* ── Calendar slot image picker ── */
  .cal-img-strip { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:10px; padding:12px; background:rgba(255,255,255,0.02); border-radius:10px; border:1px solid rgba(255,255,255,0.05); animation: scaleIn 0.2s both; }
  .cal-img-thumb {
    width:100%; height:200px; border-radius:12px; overflow:hidden; cursor:pointer;
    border:2px solid rgba(255,255,255,0.07); transition:all 0.2s cubic-bezier(0.34,1.56,0.64,1);
    position:relative;
  }
  .cal-img-thumb:hover { border-color:rgba(108,126,248,0.6); transform:scale(1.08); box-shadow:0 4px 12px rgba(0,0,0,0.4); z-index:1; }
  .cal-img-thumb.selected { border-color:#6c7ef8; box-shadow:0 0 0 2px rgba(108,126,248,0.3), 0 4px 12px rgba(0,0,0,0.4); transform:scale(1.05); z-index:1; }
  .cal-img-thumb img { width:100%; height:100%; object-fit:contain; display:block; }
  .cal-img-thumb.selected img { filter:brightness(0.8); }
  .cal-img-thumb .cal-img-check {
    position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
    font-size:16px; color:white; text-shadow:0 1px 3px rgba(0,0,0,0.8);
    animation: checkPop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
  }
  /* selected image preview panel (calendar picker) */
  .cal-img-preview {
    display:flex; align-items:flex-start; flex-direction:column; gap:12px; margin-top:12px; padding:14px;
    background:rgba(108,126,248,0.07); border:1px solid rgba(108,126,248,0.22);
    border-radius:10px; animation: scaleIn 0.2s both;
  }
  .cal-img-preview img {
    width:100%; height:480px; border-radius:12px; object-fit:contain;
    box-shadow:0 8px 32px rgba(0,0,0,0.55);
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function Spinner({ size = 18, color = D.accent }) {
  return <span style={{ display: "inline-block", width: size, height: size, border: `2px solid rgba(108,126,248,0.2)`, borderTopColor: color, borderRadius: "50%", animation: "spin 0.75s linear infinite", flexShrink: 0 }} />;
}

function Alert({ type = "info", icon, title, body }) {
  const colors = { success: "#34d399", error: "#f87171", info: D.accent, warn: "#f59e0b" };
  return (
    <div className={`alert-box ${type}`}>
      <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div>
        {title && <div style={{ fontWeight: 700, fontSize: 13.5, color: colors[type], marginBottom: 3 }}>{title}</div>}
        {body  && <div style={{ fontSize: 13, color: D.textSec, lineHeight: 1.6 }}>{body}</div>}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, body, action }) {
  return (
    <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.7 }}>{icon}</div>
      <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 18, color: D.textPrimary, mb: 1 }}>{title}</Typography>
      {body && <Typography sx={{ fontSize: 13.5, color: D.textSec, mb: 3, maxWidth: 380, mx: "auto", lineHeight: 1.7 }}>{body}</Typography>}
      {action}
    </Box>
  );
}

function PlatformChips({ selected, onChange, disabled = false }) {
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {ALL_PLATFORMS.map(pid => {
        const m = PLATFORM_META[pid];
        const active = selected.includes(pid);
        return (
          <button key={pid} disabled={disabled} onClick={() => onChange(active ? selected.filter(p => p !== pid) : [...selected, pid])}
            className={`platform-chip${active ? " active" : ""}`}
            style={active ? { background: m.soft, borderColor: m.border, color: m.color } : {}}>
            <span>{m.icon}</span><span>{m.label}</span>{active && <span style={{ fontSize: 10, opacity: 0.8 }}>✓</span>}
          </button>
        );
      })}
    </Box>
  );
}

// ─── Auto-Post Setup Guide ────────────────────────────────────────────────────
function openAutoPostGuide() {
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>ZenithPost — Auto-Post Setup Guide</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Sans',sans-serif;font-size:13.5px;color:#1a1d2e;background:#fff;padding:48px 56px;max-width:820px;margin:0 auto;line-height:1.7}
  @media print{body{padding:32px 40px}@page{margin:1.2cm 1.5cm}}
  h1{font-family:'Plus Jakarta Sans',sans-serif;font-size:26px;font-weight:800;color:#1a1d2e;margin-bottom:4px}
  .subtitle{font-size:13px;color:#6b7390;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #eceef5}
  h2{font-family:'Plus Jakarta Sans',sans-serif;font-size:16px;font-weight:700;color:#1a1d2e;margin:28px 0 10px;display:flex;align-items:center;gap:8px}
  h2 .num{width:26px;height:26px;border-radius:8px;background:#6c7ef8;color:#fff;font-size:12px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0}
  h3{font-size:13.5px;font-weight:700;color:#1a1d2e;margin:14px 0 6px}
  p{margin-bottom:8px;color:#3a3f5c}
  ul,ol{padding-left:18px;margin-bottom:10px;color:#3a3f5c}
  li{margin-bottom:4px}
  .platform-block{border-radius:10px;padding:14px 16px;margin-bottom:12px;border-left:4px solid}
  .platform-block.li{background:#f0f6ff;border-color:#0A66C2}
  .platform-block.ig{background:#fff0f5;border-color:#E1306C}
  .platform-block.fb{background:#f0f5ff;border-color:#1877F2}
  .platform-block h3{margin-top:0}
  .warn-box{background:#fffbeb;border:1px solid #f59e0b;border-radius:8px;padding:12px 14px;margin:10px 0;font-size:13px;color:#92400e}
  .warn-box strong{color:#b45309}
  .tip-box{background:#f0fdf4;border:1px solid #34d399;border-radius:8px;padding:12px 14px;margin:10px 0;font-size:13px;color:#065f46}
  .step{display:flex;gap:10px;margin-bottom:8px;align-items:flex-start}
  .step-num{width:22px;height:22px;border-radius:50%;background:#6c7ef8;color:#fff;font-size:11px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px}
  .step-text{flex:1;color:#3a3f5c}
  .divider{border:none;border-top:1px solid #eceef5;margin:24px 0}
  .header-row{display:flex;align-items:center;gap:14px;margin-bottom:24px}
  .logo-box{width:48px;height:48px;border-radius:12px;background:linear-gradient(135deg,#6c7ef8,#a37bf8);display:flex;align-items:center;justify-content:center;font-size:24px;flex-shrink:0}
  table{width:100%;border-collapse:collapse;margin:10px 0 16px;font-size:12.5px}
  th{background:#f5f6fa;text-align:left;padding:8px 10px;font-weight:700;color:#1a1d2e;border:1px solid #e5e7ef}
  td{padding:8px 10px;border:1px solid #e5e7ef;color:#3a3f5c;vertical-align:top}
  .print-btn{display:inline-flex;align-items:center;gap:8px;padding:9px 18px;border-radius:8px;background:#6c7ef8;color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:700;border:none;cursor:pointer;margin-bottom:24px}
  .print-btn:hover{background:#5a6de0}
  .footer{margin-top:36px;padding-top:16px;border-top:2px solid #eceef5;font-size:11.5px;color:#9ca3b0;text-align:center}
</style>
</head>
<body>
<div class="header-row">
  <div class="logo-box">📡</div>
  <div>
    <h1>Auto-Post Setup Guide</h1>
    <div class="subtitle" style="border:none;padding:0;margin:0">ZenithPost — Everything you need to connect, schedule, and publish your content automatically.</div>
  </div>
</div>
<button class="print-btn" onclick="window.print()">🖨 Print / Save as PDF</button>
<hr class="divider"/>

<h2><span class="num">1</span> Connecting Your Social Media Accounts</h2>
<p>Before you can schedule or post content, you need to connect your social accounts. This is a one-time setup. Click <strong>Connect / Manage Accounts</strong> in the Connected Accounts tab — this opens a secure authorization page where you connect your accounts.</p>

<div class="platform-block li">
  <h3>💼 LinkedIn</h3>
  <p><strong>How to connect:</strong> You must log in with the <em>personal LinkedIn account that is an admin</em> of your Company Page. LinkedIn does not allow connecting a Company Page directly — the connection goes through the personal account that manages it.</p>
  <div class="tip-box">✅ <strong>Tip:</strong> If you have multiple LinkedIn accounts, make sure you are signed into the one that has Admin access to your Company Page before clicking Connect.</div>
</div>

<div class="platform-block ig">
  <h3>📸 Instagram</h3>
  <p><strong>How to connect:</strong> Your Instagram account <em>must be a Business or Creator account</em> and <em>must be linked to a Facebook Page</em> before it can be connected.</p>
  <div class="warn-box">⚠️ <strong>Common issue:</strong> If your Instagram account is not connected to a Facebook Page, the connection will fail. To fix this: go to <strong>Instagram → Settings → Account → Switch to Professional Account</strong>, then link an existing Facebook Page (or create one).</div>
  <div class="tip-box">✅ Once your Instagram is linked to a Facebook Page, the connection through ZenithPost will work correctly.</div>
</div>

<div class="platform-block fb">
  <h3>📘 Facebook</h3>
  <p><strong>How to connect:</strong> ZenithPost publishes to <em>Facebook Pages only</em> (not personal profiles). You must be an Admin of the Page. Log in with the personal Facebook account that has Admin access to your Page.</p>
  <div class="tip-box">✅ <strong>Tip:</strong> If you manage multiple Pages, you can select which ones to connect during the authorization flow.</div>
</div>

<p style="margin-top:12px"><strong>After connecting:</strong> Click <strong>Refresh Status</strong> on the Connected Accounts tab to confirm your accounts show as connected (green indicator).</p>

<hr class="divider"/>

<h2><span class="num">2</span> How Auto-Posting Works (Overview)</h2>
<p>ZenithPost AI generates all your content (posts, blogs, newsletters) from a single topic. Once content is generated, you have three ways to publish it:</p>
<table>
  <thead><tr><th>Method</th><th>Best for</th><th>Where to find it</th></tr></thead>
  <tbody>
    <tr><td><strong>📅 Editorial Calendar</strong></td><td>Scheduling 30 posts across 4 weeks automatically</td><td>Content History → select topic → Calendar tab → Schedule All Posts</td></tr>
    <tr><td><strong>🗓 Schedule a Post</strong></td><td>Scheduling one specific post for a future date/time</td><td>Auto Post → Create Post tab → Schedule for Later</td></tr>
    <tr><td><strong>⚡ Post Now</strong></td><td>Publishing a post immediately</td><td>Auto Post → Create Post tab → Post Now</td></tr>
  </tbody>
</table>

<hr class="divider"/>

<h2><span class="num">3</span> Editorial Calendar — 30-Day Auto Schedule</h2>
<p>The Editorial Calendar is the most powerful feature. When you generate content, ZenithPost AI automatically creates a <strong>30-post, 4-week publishing schedule</strong> across LinkedIn, Instagram, and Facebook — with each post assigned to the optimal day, time, and week for your specific audience.</p>
<h3>How to use it:</h3>
<div class="step"><span class="step-num">1</span><span class="step-text">Generate content from the <strong>Home page</strong> for your topic.</span></div>
<div class="step"><span class="step-num">2</span><span class="step-text">Go to <strong>Content History</strong> and find your generated run.</span></div>
<div class="step"><span class="step-num">3</span><span class="step-text">Expand the topic, click the <strong>📅 Calendar</strong> tab, then click <strong>"Schedule All Posts"</strong>.</span></div>
<div class="step"><span class="step-num">4</span><span class="step-text">Review all 30 posts organized by week. Toggle off any posts you don't want, and optionally attach an image per post.</span></div>
<div class="step"><span class="step-num">5</span><span class="step-text">Click <strong>"Confirm Posts"</strong> — all posts are queued and will publish automatically on their assigned dates and times.</span></div>
<div class="tip-box">✅ <strong>Note:</strong> All calendar times are in <strong>Central Time (CT)</strong>. Posts publish automatically — you don't need to do anything once scheduled.</div>
<div class="warn-box">⚠️ Make sure all platforms in the calendar are connected before scheduling. Unconnected platforms are flagged with a warning.</div>

<hr class="divider"/>

<h2><span class="num">4</span> Scheduling an Individual Post</h2>
<p>To schedule a single post for a specific date and time:</p>
<div class="step"><span class="step-num">1</span><span class="step-text">Go to the <strong>Auto Post → Create Post</strong> tab.</span></div>
<div class="step"><span class="step-num">2</span><span class="step-text">Select the <strong>platform</strong> (LinkedIn, Instagram, or Facebook).</span></div>
<div class="step"><span class="step-num">3</span><span class="step-text">Select the post content. Add an image if needed (Instagram requires one).</span></div>
<div class="step"><span class="step-num">4</span><span class="step-text">Choose <strong>"Schedule for Later"</strong>, pick your date and time, and select your timezone.</span></div>
<div class="step"><span class="step-num">5</span><span class="step-text">Click <strong>Schedule Post</strong>. The post appears in the <strong>Scheduled</strong> tab.</span></div>

<hr class="divider"/>

<h2><span class="num">5</span> Posting Immediately (Post Now)</h2>
<p>To publish a post right away without scheduling:</p>
<div class="step"><span class="step-num">1</span><span class="step-text">Go to the <strong>Auto Post → Create Post</strong> tab.</span></div>
<div class="step"><span class="step-num">2</span><span class="step-text">Select the platform, choose content, and add an image if needed.</span></div>
<div class="step"><span class="step-num">3</span><span class="step-text">Click <strong>"Publish Now"</strong>. The post publishes within a few seconds.</span></div>
<div class="step"><span class="step-num">4</span><span class="step-text">The published post appears in your <strong>History</strong> tab.</span></div>

<hr class="divider"/>

<h2><span class="num">6</span> Viewing &amp; Managing Scheduled Posts</h2>
<p>All scheduled (but not yet published) posts are in the <strong>Scheduled</strong> tab. From here you can:</p>
<ul>
  <li>See the platform, content preview, and scheduled date/time in your timezone for each post</li>
  <li>Cancel a scheduled post before it goes out by clicking the delete icon</li>
  <li>Monitor which posts are coming up next</li>
</ul>
<p>Once a post publishes, it moves automatically to the <strong>History</strong> tab with its published timestamp in your timezone.</p>

<hr class="divider"/>

<h2><span class="num">7</span> Troubleshooting</h2>
<table>
  <thead><tr><th>Issue</th><th>What to do</th></tr></thead>
  <tbody>
    <tr><td>Instagram won't connect</td><td>Check that your Instagram is a Business/Creator account and is linked to a Facebook Page in Instagram settings.</td></tr>
    <tr><td>LinkedIn page doesn't appear</td><td>Make sure you are logged into LinkedIn with the personal account that is an admin of the Company Page.</td></tr>
    <tr><td>Post fails after scheduling</td><td>Check the History tab for the error. Usually caused by a disconnected account — reconnect and reschedule.</td></tr>
    <tr><td>Editorial Calendar not loading</td><td>The calendar only generates when "Auto Posting" is enabled during content generation. Enable it on the Home page before generating.</td></tr>
    <tr><td>Images not showing in calendar</td><td>Images must be generated during the content generation run. Regenerate with image generation enabled to get new images.</td></tr>
  </tbody>
</table>

<div class="footer">ZenithPost Auto-Post Setup Guide &nbsp;·&nbsp; Questions? Email us at <a href="mailto:info@zenithpost.ai" style="color:#6c7ef8">info@zenithpost.ai</a></div>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
}

function formatScheduledDate(isoStr, tz = "UTC") {
  if (!isoStr) return "—";
  try {
    return new Date(isoStr).toLocaleString("en-US", { timeZone: tz, month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return isoStr; }
}

// Interpret a datetime-local value ("YYYY-MM-DDTHH:MM") as being in the given
// timezone rather than the browser's local timezone, and return a UTC ISO string.
function datetimeLocalToUTC(value, tz) {
  if (!value) return "";
  try {
    const [datePart, timePart] = value.split("T");
    const [h, m] = timePart.split(":").map(Number);
    const noonUTC   = new Date(`${datePart}T12:00:00Z`);
    const noonInTz  = new Date(noonUTC.toLocaleString("en-US", { timeZone: tz }));
    const diffMs    = ((h - noonInTz.getHours()) * 60 + (m - noonInTz.getMinutes())) * 60000;
    return new Date(noonUTC.getTime() + diffMs).toISOString();
  } catch { return new Date(value).toISOString(); }
}

function truncate(str, n = 120) {
  if (!str) return "";
  return str.length > n ? str.slice(0, n) + "…" : str;
}

// ─── Timezone helpers ─────────────────────────────────────────────────────────
const _TZ_ABBR = { CDT:"CT",CST:"CT",EDT:"ET",EST:"ET",PDT:"PT",PST:"PT",MDT:"MT",MST:"MT",AKDT:"AKT",AKST:"AKT",HST:"HT",HDT:"HT",AST:"AT",ADT:"AT" };
function _validTz(tz) {
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return tz; }
  catch { return "America/Chicago"; }
}
function tzAbbr(ianaTimezone) {
  try {
    const raw = new Intl.DateTimeFormat("en-US", { timeZone: ianaTimezone, timeZoneName: "short" })
      .formatToParts(new Date()).find(p => p.type === "timeZoneName")?.value || "CT";
    return _TZ_ABBR[raw] || raw;
  } catch { return "CT"; }
}

// ─── Calendar slot date calculator ───────────────────────────────────────────
// Uses slot.timezone (set from the client's browser at pipeline run time).
// slotToISO converts the slot's local time to UTC ISO for the scheduling API.
function slotToISO(slot) {
  const tz = _validTz(slot.timezone || "America/Chicago");
  const dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const now = new Date();
  const todayIdx = now.getDay();
  const targetIdx = dayNames.indexOf(slot.day_of_week);
  const weekOffset = (slot.week_number - 1) * 7;
  const daysUntil = (targetIdx - todayIdx + 7) % 7 || 7;
  const target = new Date(now);
  target.setDate(now.getDate() + weekOffset + daysUntil);
  const dateStr = target.toLocaleDateString("en-CA", { timeZone: tz });
  const [h, m] = slot.time.split(":").map(Number);
  const noonUTC  = new Date(`${dateStr}T12:00:00Z`);
  const noonInTz = new Date(noonUTC.toLocaleString("en-US", { timeZone: tz }));
  const diffMs   = ((h - noonInTz.getHours()) * 60 + (m - noonInTz.getMinutes())) * 60000;
  return new Date(noonUTC.getTime() + diffMs).toISOString();
}

// Display calendar slot time in the slot's own timezone
function formatSlotDate(slot) {
  const tz = _validTz(slot.timezone || "America/Chicago");
  const iso = slotToISO(slot);
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz, weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    }) + ` ${tzAbbr(tz)}`;
  } catch {
    return `${slot.day_of_week} · ${slot.time}`;
  }
}

// ─── AI Hashtag Section ───────────────────────────────────────────────────────
function HashtagSection({ postText, selectedSuggestions, onChangeSuggestions }) {
  const [loading, setLoading]   = useState(false);
  const [existing, setExisting] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const fetchedFor = useRef(null);

  useEffect(() => {
    if (!postText || fetchedFor.current === postText) return;
    fetchedFor.current = postText;
    setLoading(true);
    setSuggestions([]);
    setExisting([]);
    api.get("/social/hashtag-suggestions", { params: { text: postText.slice(0, 600) } })
      .then(r => {
        setExisting(r.data.existing || []);
        setSuggestions(r.data.suggestions || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postText]);

  const toggleSuggestion = (tag) => {
    onChangeSuggestions(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  if (!postText) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1.5 }}>
        Hashtags
      </Typography>

      {/* Already in post */}
      {existing.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: D.textMuted, mb: 0.75, fontFamily: D.fontMono, letterSpacing: "0.04em" }}>
            Already in post · included automatically
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {existing.map((tag, i) => (
              <span key={i} className="ht-chip-existing" style={{ animationDelay: `${i * 0.04}s` }}>{tag}</span>
            ))}
          </Box>
        </Box>
      )}

      {/* AI Suggestions */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
          <Typography sx={{ fontSize: 11, color: D.textMuted, fontFamily: D.fontMono, letterSpacing: "0.04em" }}>
            AI hashtag suggestions · click to add
          </Typography>
          {loading && <Spinner size={11} />}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minHeight: 30 }}>
          {loading ? (
            [60, 80, 70, 90, 65].map((w, i) => (
              <span key={i} className="ht-chip-sk sk" style={{ width: w, animationDelay: `${i * 0.1}s` }} />
            ))
          ) : suggestions.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: D.textMuted, fontStyle: "italic" }}>
              {loading ? "" : "No additional suggestions — your post already has great hashtags."}
            </Typography>
          ) : (
            suggestions.map((tag, i) => {
              const isOn = selectedSuggestions.includes(tag);
              return (
                <button key={i} onClick={() => toggleSuggestion(tag)}
                  className={`ht-chip-suggestion${isOn ? " selected" : ""}`}
                  style={{ animationDelay: `${i * 0.05}s` }}>
                  {isOn ? "✓" : "+"} {tag}
                </button>
              );
            })
          )}
        </Box>
        {selectedSuggestions.length > 0 && (
          <Typography sx={{ fontSize: 11, color: D.success, mt: 0.75, fontFamily: D.fontMono }}>
            {selectedSuggestions.length} hashtag{selectedSuggestions.length > 1 ? "s" : ""} will be appended to your post
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Schedule from Calendar Modal ─────────────────────────────────────────────
function ScheduleFromCalendarModal({ calendar, topic, connected, imageUrls = [], onClose, onDone }) {
  const [activeSlots, setActiveSlots] = useState(() => calendar.map((_, i) => i));
  const [slotImages, setSlotImages]   = useState({});
  const [submitting, setSubmitting]   = useState(false);
  const [progress, setProgress]       = useState(0);
  const [result, setResult]           = useState(null);

  const setSlotImage = (idx, url) =>
    setSlotImages(prev => ({ ...prev, [idx]: prev[idx] === url ? null : url }));

  const weeks = [...new Set(calendar.map(s => s.week_number))];

  const toggleSlot = (idx) => {
    setActiveSlots(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const IMAGE_REQUIRED    = ["instagram"];                          // hard block
  const IMAGE_RECOMMENDED = ["facebook", "linkedin"];               // soft guidance only

  // Instagram slots missing an image — these block submission
  const missingImageSlots = activeSlots.filter(i =>
    IMAGE_REQUIRED.includes(calendar[i]?.platform) && !slotImages[i]
  );

  // Facebook/LinkedIn slots missing an image — warn but don't block
  const recommendedImageSlots = activeSlots.filter(i =>
    IMAGE_RECOMMENDED.includes(calendar[i]?.platform) && !slotImages[i]
  );

  const handleConfirm = async () => {
    if (missingImageSlots.length > 0) {
      const labels = missingImageSlots.map(i => `${calendar[i].platform} (${formatSlotDate(calendar[i])})`).join(", ");
      setResult({ error: `Instagram requires an image. Please select one for: ${labels}` });
      return;
    }
    // Build slots using the slot's own timezone — slotToISO converts to proper UTC ISO
    const slots = activeSlots.map(i => ({
      content:       calendar[i].content,
      platforms:     [calendar[i].platform],
      schedule_date: slotToISO(calendar[i]),              // CT → UTC ISO
      timezone:      calendar[i].timezone || "America/Chicago",
      source_topic:  topic,
      ...(slotImages[i] ? { media_url: slotImages[i] } : {}),
    }));
    setSubmitting(true);
    setProgress(0);
    try {
      let scheduled = 0;
      for (let i = 0; i < slots.length; i++) {
        try {
          await api.post("/social/schedule-bulk", { slots: [slots[i]] });
          scheduled++;
        } catch {}
        setProgress(Math.round(((i + 1) / slots.length) * 100));
        await new Promise(r => setTimeout(r, 150));
      }
      setResult({ scheduled, total: slots.length });
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Scheduling failed" });
    } finally {
      setSubmitting(false);
    }
  };

  const anyUnconnected = activeSlots.some(i => !connected.includes(calendar[i]?.platform));

  return (
    <div className="cal-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cal-modal">

        {/* Header */}
        <div className="cal-modal-header">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "rgba(108,126,248,0.15)", border: "1px solid rgba(108,126,248,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📅</Box>
              <Box>
                <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 17, color: D.textPrimary, mb: 0.25 }}>
                  Schedule from Editorial Calendar
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: D.textSec }}>
                  {topic && `"${truncate(topic, 60)}" · `}{activeSlots.length} of {calendar.length} posts selected
                </Typography>
              </Box>
            </Box>
            {!submitting && (
              <button onClick={onClose} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "7px", width: 32, height: 32, cursor: "pointer", color: D.textMuted, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            )}
          </Box>

          {/* Times are stored in the brand's base timezone; displayed in your local time */}
          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.textMuted }}>
              🕐 All calendar times are in Central Time (CT)
            </Typography>
          </Box>

          {anyUnconnected && (
            <Box sx={{ mt: 1.5 }}>
              <Alert type="warn" icon="⚠️" body="Some selected platforms aren't connected — those posts may fail. Connect accounts in the Connected Accounts tab first." />
            </Box>
          )}
        </div>

        {/* Body — slot list */}
        <div className="cal-modal-body">
          {result ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              {result.error ? (
                <>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
                  <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 18, color: D.danger, mb: 1 }}>Something went wrong</Typography>
                  <Typography sx={{ fontSize: 13.5, color: D.textSec }}>{result.error}</Typography>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 44, marginBottom: 16 }}>🎉</div>
                  <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 22, color: D.textPrimary, mb: 1 }}>
                    {result.scheduled} post{result.scheduled !== 1 ? "s" : ""} scheduled!
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: D.textSec, mb: 3 }}>
                    They'll appear in your Scheduled tab and post automatically.
                  </Typography>
                  <button className="ap-btn ap-btn-primary" onClick={onDone}>View Scheduled Posts</button>
                </>
              )}
            </Box>
          ) : (
            weeks.map(w => {
              const weekSlots = calendar.map((s, i) => ({ ...s, _idx: i })).filter(s => s.week_number === w);
              return (
                <Box key={w} sx={{ mb: 2 }}>
                  <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "1.5px", textTransform: "uppercase", mb: 1.5, pt: 1 }}>
                    Week {w}
                  </Typography>
                  {weekSlots.map((slot) => {
                    const pm = PLATFORM_META[slot.platform];
                    const isOn = activeSlots.includes(slot._idx);
                    const isConn = connected.includes(slot.platform);
                    if (!pm) return null;
                    return (
                      <div key={slot._idx} className="cal-slot-row" style={{ opacity: isOn ? 1 : 0.45, transition: "opacity 0.2s" }}>
                        <button
                          onClick={() => toggleSlot(slot._idx)}
                          className={`cal-slot-toggle${isOn ? " on" : ""}`}
                        >
                          {isOn && <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>✓</span>}
                        </button>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13, background: pm.soft, border: `1px solid ${pm.border}`, borderRadius: "6px", padding: "2px 9px", color: pm.color, fontWeight: 700, fontFamily: D.fontMono, fontSize: 11 }}>
                              {pm.icon} {pm.label}
                            </span>
                            <span style={{ fontFamily: D.fontMono, fontSize: 11, color: D.accent, background: "rgba(108,126,248,0.08)", border: "1px solid rgba(108,126,248,0.18)", borderRadius: "5px", padding: "2px 8px" }}>
                              {slot.day_of_week} · {slot.time} · {formatSlotDate(slot)}
                            </span>
                            {!isConn && <span style={{ fontSize: 10.5, color: D.warn, fontFamily: D.fontMono }}>⚠ not connected</span>}
                          </Box>
                          <Typography sx={{ fontSize: 12.5, color: D.textSec, lineHeight: 1.6, mb: 0.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {slot.content}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: D.textMuted, fontStyle: "italic", mb: imageUrls.length > 0 && ["instagram", "facebook", "linkedin"].includes(slot.platform) ? 1 : 0 }}>
                            💡 {slot.reasoning}
                          </Typography>
                          {imageUrls.length > 0 && ["instagram", "facebook", "linkedin"].includes(slot.platform) && isOn && (
                            <Box>
                              {!slotImages[slot._idx] && (
                                <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: slot.platform === "instagram" ? D.danger : D.textMuted, mb: 0.75 }}>
                                  🖼 {slot.platform === "instagram"
                                    ? "Image required — select one below"
                                    : "Attach image (recommended for better reach)"}
                                </Typography>
                              )}
                              <div className="cal-img-strip">
                                {imageUrls.map((url, ii) => (
                                  <div
                                    key={ii}
                                    className={`cal-img-thumb${slotImages[slot._idx] === url ? " selected" : ""}`}
                                    onClick={(e) => { e.stopPropagation(); setSlotImage(slot._idx, url); }}
                                    title={`Image ${ii + 1}`}
                                  >
                                    <img src={url} alt={`img ${ii + 1}`} loading="lazy" />
                                    {slotImages[slot._idx] === url && <div className="cal-img-check">✓</div>}
                                  </div>
                                ))}
                              </div>
                              {slotImages[slot._idx] && (
                                <div className="cal-img-preview">
                                  <img src={slotImages[slot._idx]} alt="Selected" />
                                  <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: D.success, mb: 0.4 }}>✓ Image attached</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: D.textMuted, mb: 1 }}>This image will go out with the post.</Typography>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setSlotImage(slot._idx, slotImages[slot._idx]); }}
                                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, cursor: "pointer", color: D.textMuted, fontSize: 11, padding: "4px 10px", fontFamily: D.fontBody }}
                                    >✕ Remove</button>
                                  </Box>
                                </div>
                              )}
                            </Box>
                          )}
                        </Box>
                      </div>
                    );
                  })}
                </Box>
              );
            })
          )}
        </div>

        {/* Footer */}
        {!result && (
          <div className="cal-modal-footer">
            {submitting && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                  <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.textMuted }}>Scheduling posts…</Typography>
                  <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.accent }}>{progress}%</Typography>
                </Box>
                <div className="progress-bar-wrap">
                  <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                </div>
              </Box>
            )}
            {missingImageSlots.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Alert
                  type="warn"
                  icon="📸"
                  body={
                    imageUrls.length > 0
                      ? `Instagram requires an image — please select one for ${missingImageSlots.length} Instagram slot${missingImageSlots.length !== 1 ? "s" : ""} above.`
                      : `${missingImageSlots.length} Instagram slot${missingImageSlots.length !== 1 ? "s" : ""} need an image but no images were generated. Deselect those slots or regenerate with image content.`
                  }
                />
              </Box>
            )}
            {missingImageSlots.length === 0 && recommendedImageSlots.length > 0 && imageUrls.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Alert
                  type="info"
                  icon="💡"
                  body={`${recommendedImageSlots.length} Facebook/LinkedIn slot${recommendedImageSlots.length !== 1 ? "s" : ""} have no image selected. Adding an image improves reach — you can still proceed without one.`}
                />
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12.5, color: D.textMuted }}>
                {activeSlots.length} post{activeSlots.length !== 1 ? "s" : ""} · select all or deselect individually above
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={onClose} disabled={submitting}>Cancel</button>
                <button
                  className="ap-btn ap-btn-primary"
                  onClick={handleConfirm}
                  disabled={submitting || activeSlots.length === 0 || missingImageSlots.length > 0}
                  style={{ minWidth: 160 }}
                >
                  {submitting ? <Spinner size={14} color="white" /> : "📅"}
                  {submitting ? `Scheduling…` : `Confirm ${activeSlots.length} Posts`}
                </button>
              </Box>
            </Box>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Connected Accounts ──────────────────────────────────────────────────
function ConnectedAccountsTab({ connected, hasProfile, onRefresh, loading }) {
  const [connecting, setConnecting] = useState(false);
  const [connectErr, setConnectErr] = useState("");

  const handleConnect = async () => {
    setConnecting(true);
    setConnectErr("");
    // Open the window immediately (synchronous) so the browser doesn't block it as a popup
    const win = window.open("about:blank", "_blank", "width=700,height=700");
    try {
      const res = await api.get("/social/connect-url");
      win.location.href = res.data.url;
      setTimeout(onRefresh, 3000);
    } catch (e) {
      win.close();
      setConnectErr(e.response?.data?.detail || "Failed to get connect URL");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Box sx={{ animation: "fadeUp 0.35s both" }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 20, color: D.textPrimary, mb: 0.75 }}>
          Connected Accounts
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: D.textSec, lineHeight: 1.7 }}>
          Connect your social accounts once — ZenithPost handles the rest. Secured.
        </Typography>
      </Box>
      {connectErr && <Box sx={{ mb: 2 }}><Alert type="error" icon="⚠️" body={connectErr} /></Box>}
      <Alert type="info" icon="🔑" body="Clicking Connect opens a secure Ayrshare page where you authorize your accounts. Once done, return here and click Refresh." />
      <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        {ALL_PLATFORMS.map(pid => {
          const m = PLATFORM_META[pid];
          const isConnected = connected.includes(pid);
          return (
            <div key={pid} className={`platform-tile${isConnected ? " connected" : ""}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: m.soft, border: `1px solid ${m.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{m.icon}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: D.textPrimary }}>{m.label}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span className={`status-dot ${isConnected ? "connected" : "disconnected"}`} />
                  <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: isConnected ? D.success : D.textMuted, fontWeight: 600 }}>
                    {isConnected ? "Connected" : "Not connected"}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 12, color: D.textMuted, mb: 2, lineHeight: 1.6 }}>{m.note}</Typography>
            </div>
          );
        })}
      </Box>
      <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <button className="ap-btn ap-btn-primary" onClick={handleConnect} disabled={!hasProfile || connecting}>
          {connecting ? <Spinner size={14} color="white" /> : "🔗"} Connect / Manage Accounts
        </button>
        <button className="ap-btn ap-btn-ghost" onClick={onRefresh} disabled={loading}>
          {loading ? <Spinner size={14} /> : "↻"} Refresh Status
        </button>
      </Box>
      {connected.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Alert type="success" icon="✅" title="You're all set!" body={`${connected.length} platform${connected.length > 1 ? "s" : ""} connected. Head to Create Post to start publishing.`} />
        </Box>
      )}
    </Box>
  );
}

// ─── Tab: Create Post ─────────────────────────────────────────────────────────
function CreatePostTab({ connected, onPostSuccess, initialCalendar, initialTopic, initialImageUrls = [] }) {
  const [recentContent, setRecentContent] = useState([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [expandedGeneration, setExpandedGeneration] = useState(null);
  const [selectedPost, setSelectedPost]   = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [postType, setPostType]           = useState("now");
  const [scheduleDate, setScheduleDate]   = useState("");
  const [timezone, setTimezone]           = useState("America/New_York");
  const [posting, setPosting]             = useState(false);
  const [postResult, setPostResult]       = useState(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showCalModal, setShowCalModal]   = useState(!!initialCalendar);
  const composerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/pipeline/tasks");
        const tasks = (res.data?.tasks || []).filter(t => t.status === "complete" && t.result).slice(0, 5);
        setRecentContent(tasks);
      } catch { setRecentContent([]); }
      finally { setContentLoading(false); }
    })();
  }, []);

  // Reset hashtag suggestions and image when post changes
  useEffect(() => { setSelectedSuggestions([]); setSelectedImageUrl(null); }, [selectedPost]);

  const handleSelectPost = (text, sourceTopic, imageUrls) => {
    setSelectedPost({ text, sourceTopic, imageUrls: imageUrls || [] });
    setPostResult(null);
    setTimeout(() => composerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
  };

  const IMAGE_REQUIRED_PLATFORMS = ["instagram"];  // hard block; facebook/linkedin show soft guidance only
  const needsImage = selectedPlatforms.some(p => IMAGE_REQUIRED_PLATFORMS.includes(p));

  const handleSubmit = async () => {
    if (!selectedPost || selectedPlatforms.length === 0) return;

    // Instagram requires an image
    if (needsImage && !selectedImageUrl) {
      setPostResult({ type: "error", msg: "Instagram requires an image. Please select one before posting." });
      return;
    }

    setPosting(true);
    setPostResult(null);
    const finalContent = selectedSuggestions.length > 0
      ? `${selectedPost.text.trimEnd()} ${selectedSuggestions.join(" ")}`
      : selectedPost.text;
    try {
      if (postType === "now") {
        await api.post("/social/post", { content: finalContent, platforms: selectedPlatforms, source_topic: selectedPost.sourceTopic, media_url: selectedImageUrl || undefined });
        setPostResult({ type: "success", msg: `Posted to ${selectedPlatforms.map(p => PLATFORM_META[p].label).join(", ")} successfully!` });
        onPostSuccess?.("now");
      } else {
        if (!scheduleDate) { setPostResult({ type: "error", msg: "Please select a date and time." }); setPosting(false); return; }
        const iso = datetimeLocalToUTC(scheduleDate, timezone);
        await api.post("/social/schedule", { content: finalContent, platforms: selectedPlatforms, schedule_date: iso, timezone, source_topic: selectedPost.sourceTopic, media_url: selectedImageUrl || undefined });
        setPostResult({ type: "success", msg: `Scheduled for ${formatScheduledDate(iso, timezone)} (${timezone})` });
        onPostSuccess?.("schedule");
      }
      setSelectedPost(null);
      setSelectedPlatforms([]);
      setScheduleDate("");
      setSelectedSuggestions([]);
      setSelectedImageUrl(null);
    } catch (e) {
      setPostResult({ type: "error", msg: e.response?.data?.detail || "Failed to post. Please try again." });
    } finally {
      setPosting(false);
    }
  };

  const nowMin = new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16);

  return (
    <Box sx={{ animation: "fadeUp 0.35s both" }}>
      {/* Calendar modal */}
      {showCalModal && initialCalendar && (
        <ScheduleFromCalendarModal
          calendar={initialCalendar}
          topic={initialTopic || ""}
          connected={connected}
          imageUrls={initialImageUrls}
          onClose={() => setShowCalModal(false)}
          onDone={() => { setShowCalModal(false); onPostSuccess?.("schedule"); }}
        />
      )}

      {connected.length === 0 && (
        <Box sx={{ mb: 3 }}>
          <Alert type="warn" icon="⚠️" title="No accounts connected" body="Connect at least one social account in the Connected Accounts tab before posting." />
        </Box>
      )}

      {/* Schedule from Calendar card — shows when navigated from ContentHistory */}
      {initialCalendar && (
        <Box sx={{ mb: 3, p: 3, background: "rgba(108,126,248,0.06)", border: "1px solid rgba(108,126,248,0.22)", borderRadius: "14px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 15, color: D.textPrimary, mb: 0.5 }}>
                📅 Editorial Calendar Ready
              </Typography>
              <Typography sx={{ fontSize: 13, color: D.textSec }}>
                {initialCalendar.length} posts across {[...new Set(initialCalendar.map(s => s.platform))].map(p => PLATFORM_META[p]?.label).filter(Boolean).join(", ")} · schedule them all in one click.
              </Typography>
            </Box>
            <button className="ap-btn ap-btn-primary" onClick={() => setShowCalModal(true)}>
              ⚡ Schedule {initialCalendar.length} Posts
            </button>
          </Box>
        </Box>
      )}

      {/* Content Selector */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 6, height: 20, borderRadius: "3px", background: `linear-gradient(180deg, ${D.accent}, ${D.purple})` }} />
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 16, color: D.textPrimary }}>Select Content</Typography>
          <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: D.textMuted }}>last 5 generations</Typography>
        </Box>

        {contentLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {[1, 2, 3].map(i => <Box key={i} className="sk" sx={{ height: 60, borderRadius: "12px" }} />)}
          </Box>
        ) : recentContent.length === 0 ? (
          <Box sx={{ p: 3, border: `1px solid ${D.border}`, borderRadius: "12px", textAlign: "center" }}>
            <Typography sx={{ fontSize: 13.5, color: D.textSec }}>No content generated yet. Run a generation from the Home page first.</Typography>
          </Box>
        ) : (
          recentContent.map((entry, idx) => {
            const result = entry.result || entry;
            const topic = entry.topic || result.topic || "Untitled";
            const createdAt = entry.created_at ? new Date(entry.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
            const isOpen = expandedGeneration === idx;
            return (
              <div key={entry.task_id || idx} className="content-gen-card">
                <div className="content-gen-head" onClick={() => setExpandedGeneration(isOpen ? null : idx)}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: D.success, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 13.5, color: D.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{topic}</Typography>
                      {createdAt && <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted }}>{createdAt}</Typography>}
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, ml: 1, flexShrink: 0, flexWrap: "wrap" }}>
                      {ALL_PLATFORMS.map(pid => {
                        const posts = result[CONTENT_PLATFORM_MAP[pid]];
                        if (!posts?.length) return null;
                        return <span key={pid} style={{ fontSize: 11, background: "rgba(255,255,255,0.06)", borderRadius: "5px", padding: "2px 7px", color: D.textSec }}>{PLATFORM_META[pid].icon} {posts.length}</span>;
                      })}
                    </Box>
                  </Box>
                  <span className={`chevron${isOpen ? " open" : ""}`} style={{ color: D.textMuted, fontSize: 18, marginLeft: 12, flexShrink: 0 }}>▾</span>
                </div>
                <Collapse in={isOpen}>
                  <Box sx={{ px: 2.5, pb: 2.5 }}>
                    {ALL_PLATFORMS.map(pid => {
                      const posts = result[CONTENT_PLATFORM_MAP[pid]];
                      if (!posts?.length) return null;
                      const m = PLATFORM_META[pid];
                      return (
                        <Box key={pid} sx={{ mb: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <span style={{ fontSize: 14 }}>{m.icon}</span>
                            <Typography sx={{ fontFamily: D.fontMono, fontSize: 10.5, color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{m.label}</Typography>
                          </Box>
                          {posts.map((post, pi) => (
                            <div key={pi} className={`post-preview-card${selectedPost?.text === post ? " selected" : ""}`}>
                              <Typography sx={{ fontSize: 13, color: "#d0d5e8", lineHeight: 1.7, whiteSpace: "pre-wrap", mb: 1.5, pr: 1 }}>{truncate(post, 200)}</Typography>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted }}>Variant #{pi + 1}</Typography>
                                <button
                                  className={`ap-btn ap-btn-sm ${selectedPost?.text === post ? "ap-btn-ghost" : "ap-btn-primary"}`}
                                  style={selectedPost?.text === post ? { background: D.accentSoft, borderColor: D.accentBorder, color: D.accent } : {}}
                                  onClick={() => handleSelectPost(post, topic, result.image_urls)}
                                >
                                  {selectedPost?.text === post ? "✓ Selected" : "Use This"}
                                </button>
                              </Box>
                            </div>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </div>
            );
          })
        )}
      </Box>

      {/* Composer */}
      <Box ref={composerRef} sx={{ p: 3, background: D.surface, border: `1px solid ${selectedPost ? D.accentBorder : D.border}`, borderRadius: "16px", transition: "border-color 0.3s" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box sx={{ width: 6, height: 20, borderRadius: "3px", background: `linear-gradient(180deg, ${D.accent}, ${D.purple})` }} />
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 16, color: D.textPrimary }}>Composer</Typography>
        </Box>

        {/* Selected post preview */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1 }}>Selected Post</Typography>
          {selectedPost ? (
            <Box sx={{ p: 2.5, background: "rgba(108,126,248,0.06)", border: `1px solid ${D.accentBorder}`, borderRadius: "10px", position: "relative" }}>
              {selectedPost.sourceTopic && (
                <Typography sx={{ fontFamily: D.fontMono, fontSize: 10.5, color: D.accent, mb: 1, letterSpacing: "0.05em" }}>From: {selectedPost.sourceTopic}</Typography>
              )}
              <Typography sx={{ fontSize: 13.5, color: D.textPrimary, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{selectedPost.text}</Typography>
              <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => { setSelectedPost(null); setSelectedPlatforms([]); setSelectedSuggestions([]); }} style={{ marginTop: 14 }}>✕ Clear</button>
            </Box>
          ) : (
            <Box sx={{ p: 2.5, background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: "10px", textAlign: "center" }}>
              <Typography sx={{ fontSize: 13.5, color: D.textMuted }}>Select a post from your content above to compose</Typography>
            </Box>
          )}
        </Box>

        {/* AI Hashtag Suggestions */}
        {selectedPost && (
          <HashtagSection
            postText={selectedPost.text}
            selectedSuggestions={selectedSuggestions}
            onChangeSuggestions={setSelectedSuggestions}
          />
        )}

        {/* Image picker — shown when a post with images is selected */}
        {selectedPost && selectedPost.imageUrls?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <div className="img-picker-wrap">
              <div className="img-picker-header">
                <div className="img-picker-label">
                  <div className="img-picker-label-icon">🖼</div>
                  <Box>
                    <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 13.5, color: D.textPrimary, lineHeight: 1.2 }}>
                      Attach an Image
                    </Typography>
                    <Typography sx={{ fontFamily: D.fontMono, fontSize: 10.5, color: needsImage ? D.danger : D.textMuted }}>
                      {needsImage ? "Required for Instagram · Recommended for Facebook & LinkedIn" : "Recommended for Facebook & LinkedIn"}
                    </Typography>
                  </Box>
                </div>
                {selectedImageUrl && (
                  <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => setSelectedImageUrl(null)} style={{ fontSize: 11.5 }}>
                    ✕ Remove
                  </button>
                )}
              </div>

              <div className="img-picker-grid">
                {selectedPost.imageUrls.map((url, i) => (
                  <div
                    key={i}
                    className={`img-picker-thumb${selectedImageUrl === url ? " selected" : ""}`}
                    onClick={() => setSelectedImageUrl(selectedImageUrl === url ? null : url)}
                  >
                    <img src={url} alt={`Generated image ${i + 1}`} loading="lazy" />
                    <div className="img-overlay" />
                    <div className="img-num">{i + 1}</div>
                    {selectedImageUrl === url && <div className="img-check">✓</div>}
                  </div>
                ))}
              </div>

              {selectedImageUrl && (
                <div className="img-selected-preview">
                  <img src={selectedImageUrl} alt="Selected" />
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: D.success, mb: 0.25 }}>Image attached</Typography>
                    <Typography sx={{ fontSize: 11.5, color: D.textMuted }}>Will be included in your post. Click the image again to deselect.</Typography>
                  </Box>
                </div>
              )}

              {selectedPlatforms.includes("instagram") && !selectedImageUrl && (
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, p: "8px 12px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: "8px" }}>
                  <span style={{ fontSize: 13 }}>⚠️</span>
                  <Typography sx={{ fontSize: 12, color: D.warn }}>Instagram feed posts require an image — select one above</Typography>
                </Box>
              )}
            </div>
          </Box>
        )}

        {/* Platform selector */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1.5 }}>Publish To</Typography>
          <PlatformChips selected={selectedPlatforms} onChange={setSelectedPlatforms} disabled={!selectedPost || connected.length === 0} />
          {selectedPlatforms.some(p => !connected.includes(p)) && (
            <Typography sx={{ fontSize: 11.5, color: D.warn, mt: 1 }}>
              ⚠ {selectedPlatforms.filter(p => !connected.includes(p)).map(p => PLATFORM_META[p].label).join(", ")} not connected — may fail
            </Typography>
          )}
        </Box>

        {/* Schedule toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1.5 }}>When to Post</Typography>
          <div className="schedule-toggle" style={{ maxWidth: 320 }}>
            <button className={`schedule-toggle-btn${postType === "now" ? " active" : ""}`} onClick={() => setPostType("now")}>⚡ Post Now</button>
            <button className={`schedule-toggle-btn${postType === "schedule" ? " active" : ""}`} onClick={() => setPostType("schedule")}>🗓 Schedule</button>
          </div>
        </Box>

        {postType === "schedule" && (
          <Box sx={{ mb: 3, p: 2.5, background: "rgba(255,255,255,0.025)", border: `1px solid ${D.border}`, borderRadius: "12px", animation: "scaleIn 0.25s both" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1 }}>Date & Time</Typography>
                <input type="datetime-local" className="ap-datetime" value={scheduleDate} min={nowMin} onChange={e => setScheduleDate(e.target.value)} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1 }}>Timezone</Typography>
                <select className="ap-select" value={timezone} onChange={e => setTimezone(e.target.value)}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
                </select>
              </Box>
            </Box>
            {scheduleDate && (
              <Typography sx={{ fontSize: 12, color: D.success, mt: 1.5, fontFamily: D.fontMono }}>
                Will post: {formatScheduledDate(datetimeLocalToUTC(scheduleDate, timezone), timezone)}
              </Typography>
            )}
          </Box>
        )}

        {/* Image required warning */}
        {needsImage && !selectedImageUrl && selectedPost && (
          <Box sx={{ mb: 2 }}>
            <Alert type="warn" icon="📸" body="Instagram requires an image. Select one above before posting. Supported formats: JPG, PNG, WEBP, GIF, MP4, MOV." />
          </Box>
        )}

        {postResult && (
          <Box sx={{ mb: 2 }}>
            <Alert type={postResult.type} icon={postResult.type === "success" ? "✅" : "⚠️"} body={postResult.msg} />
          </Box>
        )}

        <button className="ap-btn ap-btn-primary"
          onClick={handleSubmit}
          disabled={!selectedPost || selectedPlatforms.length === 0 || posting || connected.length === 0 || (needsImage && !selectedImageUrl)}
          style={{ minWidth: 180 }}>
          {posting ? <Spinner size={14} color="white" /> : (postType === "now" ? "⚡" : "🗓")}
          {posting ? "Processing…" : (postType === "now" ? "Publish Now" : "Schedule Post")}
        </button>
      </Box>
    </Box>
  );
}

// ─── Tab: Scheduled Posts ─────────────────────────────────────────────────────
function ScheduledTab({ onDelete }) {
  const [posts, setPosts]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [deleting, setDeleting]         = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [deleteError, setDeleteError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await api.post("/social/sync-scheduled").catch(() => {});
      const res = await api.get("/social/scheduled");
      setPosts(res.data.posts || []);
    }
    catch { setPosts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    setDeleting(id);
    setDeleteWarning(null);
    setDeleteError(null);
    try {
      const res = await api.delete(`/social/scheduled/${id}`);
      setPosts(prev => prev.filter(p => p._id !== id));
      onDelete?.();
      if (res.data?.warning) {
        setDeleteWarning(res.data.warning);
      }
    } catch (e) {
      setDeleteError(e.response?.data?.detail || "Delete failed. Please try again.");
    } finally { setDeleting(null); setDeleteConfirm(null); }
  };

  return (
    <Box sx={{ animation: "fadeUp 0.35s both" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 20, color: D.textPrimary, mb: 0.5 }}>Scheduled Posts</Typography>
          <Typography sx={{ fontSize: 13, color: D.textSec }}>Posts queued to go live automatically.</Typography>
        </Box>
        <button className="ap-btn ap-btn-ghost" onClick={load} disabled={loading}>{loading ? <Spinner size={14} /> : "↻"} Refresh</button>
      </Box>
      {deleteError && (
        <Box sx={{ mb: 2 }}>
          <Alert type="error" icon="⚠️" body={deleteError} />
        </Box>
      )}
      {deleteWarning && (
        <Box sx={{ mb: 2 }}>
          <Alert type="warn" icon="⚠️" body={deleteWarning} />
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[1, 2, 3].map(i => <Box key={i} className="sk" sx={{ height: 100, borderRadius: "12px" }} />)}
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ border: `1px solid ${D.border}`, borderRadius: "16px" }}>
          <EmptyState icon="🗓" title="No scheduled posts" body="Create a scheduled post from the Create Post tab to see it here." />
        </Box>
      ) : (
        posts.map((post, i) => (
          <div key={post._id} className="scheduled-row" style={{ animationDelay: `${i * 0.05}s` }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: D.warn, flexShrink: 0, boxShadow: `0 0 6px rgba(245,158,11,0.5)` }} />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
                    {post.platforms?.map(pid => { const m = PLATFORM_META[pid]; if (!m) return null; return <span key={pid} style={{ fontSize: 11.5, background: m.soft, border: `1px solid ${m.border}`, borderRadius: "6px", padding: "2px 9px", color: m.color, fontWeight: 600 }}>{m.icon} {m.label}</span>; })}
                  </Box>
                  <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.accent }}>
                    🗓 {formatScheduledDate(post.scheduled_for, post.timezone || "America/Chicago")}
                    {post.timezone && <span style={{ color: D.textMuted, marginLeft: 6 }}>({post.timezone})</span>}
                  </Typography>
                  {post.source_topic && <Typography sx={{ fontFamily: D.fontMono, fontSize: 10.5, color: D.textMuted, mt: 0.25 }}>From: {post.source_topic}</Typography>}
                </Box>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                {deleteConfirm === post._id ? (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <button className="ap-btn ap-btn-danger ap-btn-sm" onClick={() => handleDelete(post._id)} disabled={deleting === post._id}>{deleting === post._id ? <Spinner size={12} color="#f87171" /> : "Confirm"}</button>
                    <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                  </Box>
                ) : (
                  <button className="ap-btn ap-btn-danger ap-btn-sm" onClick={() => setDeleteConfirm(post._id)}>🗑 Delete</button>
                )}
              </Box>
            </Box>
            <Box sx={{ p: 2, background: "rgba(255,255,255,0.025)", borderRadius: "8px", border: `1px solid ${D.border}` }}>
              <Typography sx={{ fontSize: 13, color: "#c8cede", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{truncate(post.content, 240)}</Typography>
            </Box>
          </div>
        ))
      )}
    </Box>
  );
}

// ─── Tab: Post History ────────────────────────────────────────────────────────
function HistoryTab() {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await api.post("/social/sync-scheduled").catch(() => {});
      const res = await api.get("/social/history");
      setPosts(res.data.posts || []);
    }
    catch { setPosts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ animation: "fadeUp 0.35s both" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 20, color: D.textPrimary, mb: 0.5 }}>Post History</Typography>
          <Typography sx={{ fontSize: 13, color: D.textSec }}>Content you've published to your connected accounts.</Typography>
        </Box>
        <button className="ap-btn ap-btn-ghost" onClick={load} disabled={loading}>{loading ? <Spinner size={14} /> : "↻"} Refresh</button>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[1, 2, 3].map(i => <Box key={i} className="sk" sx={{ height: 110, borderRadius: "12px" }} />)}
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ border: `1px solid ${D.border}`, borderRadius: "16px" }}>
          <EmptyState icon="📤" title="No posts yet" body="Posts you publish will appear here with links to the live content on each platform." />
        </Box>
      ) : (
        posts.map((post, i) => {
          const postedAt = post.posted_at
            ? (() => {
                const tz = _validTz(post.timezone || "America/Chicago");
                try {
                  return new Date(post.posted_at).toLocaleString("en-US", {
                    timeZone: tz, month: "short", day: "numeric", year: "numeric",
                    hour: "numeric", minute: "2-digit", hour12: true,
                  }) + ` ${tzAbbr(tz)}`;
                } catch { return post.posted_at; }
              })()
            : "—";
          const urls = post.post_urls || {};
          return (
            <div key={post._id} className="history-row" style={{ animationDelay: `${i * 0.04}s` }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 200 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: D.success, flexShrink: 0, boxShadow: `0 0 6px rgba(52,211,153,0.5)` }} />
                  <Box>
                    <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.success }}>✓ Published · {postedAt}</Typography>
                    {post.source_topic && <Typography sx={{ fontFamily: D.fontMono, fontSize: 10.5, color: D.textMuted, mt: 0.25 }}>{post.source_topic}</Typography>}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {post.platforms?.map(pid => { const m = PLATFORM_META[pid]; if (!m) return null; const url = urls[pid]; return url ? <a key={pid} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11.5, background: m.soft, border: `1px solid ${m.border}`, borderRadius: "6px", padding: "2px 9px", color: m.color, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>{m.icon} {m.label} ↗</a> : <span key={pid} style={{ fontSize: 11.5, background: m.soft, border: `1px solid ${m.border}`, borderRadius: "6px", padding: "2px 9px", color: m.color, fontWeight: 600 }}>{m.icon} {m.label}</span>; })}
                </Box>
              </Box>
              <Box sx={{ p: 2, background: "rgba(255,255,255,0.025)", borderRadius: "8px", border: `1px solid ${D.border}` }}>
                <Typography sx={{ fontSize: 13, color: "#c8cede", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{truncate(post.content, 280)}</Typography>
              </Box>
            </div>
          );
        })
      )}
    </Box>
  );
}

// ─── Feature Locked ───────────────────────────────────────────────────────────
function FeatureLocked() {
  const navigate = useNavigate();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "70vh", textAlign: "center", px: 3 }}>
      <Box sx={{ width: 72, height: 72, borderRadius: "18px", background: "rgba(255,255,255,0.04)", border: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, mb: 3 }}>🔒</Box>
      <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 26, color: D.textPrimary, mb: 1.5 }}>Auto-Posting Not Enabled</Typography>
      <Typography sx={{ fontSize: 14.5, color: D.textSec, maxWidth: 420, lineHeight: 1.8, mb: 4 }}>The auto-posting feature isn't enabled for your account. Contact your account manager to get access.</Typography>
      <button className="ap-btn ap-btn-primary" onClick={() => navigate("/home")}>← Back to Home</button>
    </Box>
  );
}

// ─── Shared Nav ───────────────────────────────────────────────────────────────
function Nav({ navigate }) {
  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 200, background: "rgba(13,14,20,0.88)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
      <Box sx={{ maxWidth: 960, margin: "0 auto", px: 3, display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
        {/* Logo + page label */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: "linear-gradient(135deg, #6c7ef8 0%, #a37bf8 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 14, color: "#fff", boxShadow: "0 4px 14px rgba(108,126,248,0.35)", animation: "logoGlow 3s ease infinite" }}>Z</Box>
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 15, color: "#f0f2f7", letterSpacing: "-0.02em" }}>
              zenithpost<span style={{ color: "#6c7ef8" }}>.ai</span>
            </Typography>
          </Box>
          <Box sx={{ width: "1px", height: 18, background: "rgba(255,255,255,0.07)" }} />
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.22)", borderRadius: 99, px: 1.25, py: 0.4 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: "#34d399" }} />
            <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 700, color: "#34d399", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Auto-Publisher
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <button onClick={() => navigate("/home")}    className="ap-btn ap-btn-ghost ap-btn-sm">← Back to Home</button>
          <button onClick={() => navigate("/history")} className="ap-btn ap-btn-ghost ap-btn-sm">History</button>
          <button onClick={() => navigate("/account")} className="ap-btn ap-btn-ghost ap-btn-sm">Account</button>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function AutoPostDashboard() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Calendar state passed from ContentHistory via navigate(state)
  const calendarState  = location.state?.calendar  || null;
  const calendarTopic  = location.state?.topic     || "";
  const calendarImages = location.state?.imageUrls || [];

  const [activeTab, setActiveTab]       = useState(calendarState ? 1 : 0);
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [connected, setConnected]       = useState([]);
  const [hasProfile, setHasProfile]     = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [initError, setInitError]       = useState("");

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await api.get("/social/status");
      setConnected(res.data.connected_platforms || []);
      setHasProfile(res.data.has_profile ?? true);
    } catch { setConnected([]); }
    finally { setStatusLoading(false); }
  }, []);

  const init = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) { navigate("/login", { replace: true }); return; }
    try {
      const me = await api.get("/auth/me");
      if (!me.data.auto_posting) { setFeatureEnabled(false); return; }
      setFeatureEnabled(true);
    } catch { setFeatureEnabled(false); return; }
    try { await api.post("/social/ensure-profile"); } catch (e) { setInitError(e.response?.data?.detail || "Failed to initialize"); return; }
    await refreshStatus();
  }, [navigate, refreshStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { init(); }, [init]);

  if (featureEnabled === null) {
    return (
      <ThemeProvider theme={muiTheme}><CssBaseline /><style>{G}</style>
        <Box sx={{ background: D.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Spinner size={32} /><Typography sx={{ fontSize: 13.5, color: D.textMuted, fontFamily: D.fontMono }}>Loading…</Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  if (featureEnabled === false) {
    return (
      <ThemeProvider theme={muiTheme}><CssBaseline /><style>{G}</style>
        <Box sx={{ background: D.bg, minHeight: "100vh" }}><Nav navigate={navigate} /><Box sx={{ maxWidth: 860, margin: "0 auto", px: { xs: 2, md: 3 } }}><FeatureLocked /></Box></Box>
      </ThemeProvider>
    );
  }

  const TAB_LABELS = [
    { label: "Connected Accounts", icon: "🔗" },
    { label: "Create Post", icon: "✍️" },
    { label: "Scheduled", icon: "🗓" },
    { label: "History", icon: "📋" },
  ];

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <style>{G}</style>
      <Box sx={{ background: D.bg, minHeight: "100vh" }}>
        <Nav navigate={navigate} />
        <Box sx={{ maxWidth: 900, margin: "0 auto", px: { xs: 2, md: 3 }, py: 4 }}>

          {/* Page Header */}
          <Box sx={{ mb: 4 }} className="fade-up">
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg, rgba(108,126,248,0.25), rgba(163,123,248,0.15))", border: `1px solid ${D.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>📡</Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 26, color: D.textPrimary, lineHeight: 1.2 }}>Auto Post</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
                  <Typography sx={{ fontSize: 13.5, color: D.textSec }}>Schedule & publish to LinkedIn, Instagram, and Facebook from one place.</Typography>
                  <button
                    onClick={openAutoPostGuide}
                    style={{ background: "rgba(108,126,248,0.08)", border: "1px solid rgba(108,126,248,0.22)", borderRadius: "8px", padding: "5px 12px", color: D.accent, fontSize: 12, fontFamily: D.fontBody, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(108,126,248,0.15)"; e.currentTarget.style.borderColor = "rgba(108,126,248,0.4)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(108,126,248,0.08)"; e.currentTarget.style.borderColor = "rgba(108,126,248,0.22)"; }}
                  >
                    📄 Setup Guide
                  </button>
                </Box>
              </Box>
            </Box>

            {/* Connection status bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, p: 1.5, background: D.surface, borderRadius: "10px", border: `1px solid ${D.border}`, flexWrap: "wrap" }}>
              <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.textMuted, letterSpacing: "0.05em" }}>CONNECTED:</Typography>
              {connected.length === 0
                ? <Typography sx={{ fontSize: 12.5, color: D.textMuted }}>None — connect accounts to start posting</Typography>
                : ALL_PLATFORMS.map(pid => {
                    const m = PLATFORM_META[pid];
                    const isOn = connected.includes(pid);
                    return (
                      <span key={pid} style={{ fontSize: 12, background: isOn ? m.soft : "rgba(255,255,255,0.04)", border: `1px solid ${isOn ? m.border : D.border}`, borderRadius: "6px", padding: "3px 10px", color: isOn ? m.color : D.textMuted, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
                        {m.icon} {m.label}{isOn ? " ✓" : ""}
                      </span>
                    );
                  })}
              {statusLoading && <Spinner size={12} />}
            </Box>
          </Box>

          {initError && <Box sx={{ mb: 3 }}><Alert type="error" icon="⚠️" title="Initialization failed" body={initError} /></Box>}

          {/* Calendar banner if navigated from ContentHistory */}
          {calendarState && activeTab !== 1 && (
            <Box sx={{ mb: 3, p: 2.5, background: "rgba(108,126,248,0.07)", border: "1px solid rgba(108,126,248,0.22)", borderRadius: "12px", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 14, color: D.textPrimary, mb: 0.25 }}>📅 Editorial Calendar loaded</Typography>
                <Typography sx={{ fontSize: 12.5, color: D.textSec }}>{calendarState.length} posts ready to schedule from "{truncate(calendarTopic, 50)}"</Typography>
              </Box>
              <button className="ap-btn ap-btn-primary ap-btn-sm" onClick={() => setActiveTab(1)}>Open Composer →</button>
            </Box>
          )}

          {/* Tabs */}
          <Box sx={{ mb: 3, borderBottom: `1px solid ${D.border}` }} className="fade-up fade-up-1">
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
              {TAB_LABELS.map((t, i) => (
                <Tab key={i} label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><span>{t.icon}</span><span>{t.label}</span></Box>} />
              ))}
            </Tabs>
          </Box>

          {/* Tab panels */}
          <Box className="fade-up fade-up-2">
            {activeTab === 0 && <ConnectedAccountsTab connected={connected} hasProfile={hasProfile} onRefresh={refreshStatus} loading={statusLoading} />}
            {activeTab === 1 && (
              <CreatePostTab
                connected={connected}
                initialCalendar={calendarState}
                initialTopic={calendarTopic}
                initialImageUrls={calendarImages}
                onPostSuccess={(type) => { setTimeout(() => setActiveTab(type === "now" ? 3 : 2), 800); }}
              />
            )}
            {activeTab === 2 && <ScheduledTab onDelete={() => {}} />}
            {activeTab === 3 && <HistoryTab />}
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 6, pt: 4, borderTop: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: "5px", background: `linear-gradient(135deg, ${D.accent}, ${D.purple})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 11, color: "white" }}>Z</Box>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 13, color: D.textPrimary }}>zenithpost<span style={{ color: D.accent }}>.ai</span></Typography>
            </Box>
            <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.textMuted }}>Powered by ZenithPost</Typography>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
